/**
 * Rich text, rendered without `dangerouslySetInnerHTML`.
 *
 * That absence is the whole security posture of ADR-0013 §4 on the render
 * side: 3C validates on save, and this file cannot un-validate, because there
 * is no path from a payload string to markup — `site/document.tsx` accounts for
 * the renderer's single `dangerouslySetInnerHTML`, the stylesheet, which no
 * payload text reaches. A `<script>` in a block's text renders as the fourteen
 * characters `&lt;script&gt;`.
 *
 * ## What this file got wrong until Wave 4D
 *
 * **Every `about` and `rich_text` block on every firm's site rendered
 * nothing.** Four ways at once, and each of them silently:
 *
 * | | the service sends | this file read |
 * |---|---|---|
 * | the document | `{nodes: [...]}` | a bare array |
 * | a node's kind | `kind` | `type` |
 * | a list's items | `[{spans: [...]}]` | `['a string']` |
 * | emphasis | `{strong: true}` / `{em: true}` | `{emphasis: 'strong'}` |
 *
 * The first alone was fatal: `list({nodes: […]})` is `[]`, so `hasRichText`
 * was false for every real body and `About` returned `null` before it rendered
 * a thing. The privacy page — one of the seven every firm gets — was blank.
 *
 * This is the fifth of its kind. The Wave 3 close's reconciliation found four
 * blocks rendering nothing against the real payload (`site-contract.test.ts`),
 * and it did not reach here because no test asserted that an `about` block's
 * *body* came out the other end — only that a page carrying one did not throw.
 * A block that renders nothing does not throw. `tests/site-contract.test.ts`
 * now asserts the words.
 *
 * ## Read tolerantly, in both shapes
 *
 * The old names are kept as fallbacks rather than deleted, which is the rule
 * the rest of `site/blocks/` follows (`BlogList`, `ImageBlock`): they cost one
 * `??` each, they are what the older fixture carries, and a renderer on a
 * firm's public website should never be the thing that is strict.
 */

import type { ReactElement, ReactNode } from 'react'

import { href, list, obj, str } from '../read'
import type { RichText, Span } from '../types'

/** One inline run. The service marks emphasis with two booleans; 3D read a
 *  single `emphasis` string. Both are accepted. */
function spans(value: unknown, key: string): ReactNode {
  const items = list(value)
  if (items.length === 0) return null
  return items.map((raw, i) => {
    const span = obj(raw) as Span & { strong?: boolean; em?: boolean }
    const text = str(span.text)
    if (!text) return null
    const link = href(span.href)
    const strong = span.strong === true || span.emphasis === 'strong'
    const em = span.em === true || span.emphasis === 'em'
    const inner = strong ? <strong>{text}</strong> : em ? <em>{text}</em> : text
    if (link) {
      // A relative link is the firm's own page; an absolute one leaves the
      // site, and a firm's site does not vouch for where it sends a visitor.
      const external = link.startsWith('http')
      return (
        <a
          key={`${key}-${i}`}
          href={link}
          {...(external ? { rel: 'noopener noreferrer nofollow', target: '_blank' } : {})}
        >
          {inner}
        </a>
      )
    }
    return <span key={`${key}-${i}`}>{inner}</span>
  })
}

/** A list's items: `{spans: [...]}` from the service, a bare string from the
 *  older shape. */
function listItems(value: unknown, key: string): ReactElement[] {
  const items: ReactElement[] = []
  list(value).forEach((raw, i) => {
    const inner = typeof raw === 'string' ? str(raw) : spans(obj(raw).spans, `${key}-${i}`)
    if (inner) items.push(<li key={`${key}-${i}`}>{inner}</li>)
  })
  return items
}

function node(raw: unknown, key: string): ReactNode {
  const item = obj(raw)
  // `kind` is the service's name (`RICH_NODE_KINDS`); `type` was 3D's guess.
  const kind = str(item.kind) ?? str(item.type)
  const inline = spans(item.spans, key)
  const body = inline ?? str(item.text)
  switch (kind) {
    case 'h2':
      return body ? <h2 key={key}>{body}</h2> : null
    case 'h3':
      return body ? <h3 key={key}>{body}</h3> : null
    // `quote` is one of the six kinds the service accepts and this file had no
    // case for it, so a firm's pull quote rendered as an ordinary paragraph.
    case 'quote':
      return body ? <blockquote key={key}>{body}</blockquote> : null
    case 'ul':
    case 'ol': {
      const children = listItems(item.items, key)
      if (children.length === 0) return null
      return kind === 'ul' ? <ul key={key}>{children}</ul> : <ol key={key}>{children}</ol>
    }
    case 'p':
    default:
      return body ? <p key={key}>{body}</p> : null
  }
}

/**
 * The nodes of a document, whichever shape it arrived in.
 *
 * `{nodes: [...]}` is the service's; a bare array was 3D's reading; a string is
 * paragraphs split on blank lines, which is neither but costs three lines and
 * cannot be wrong.
 */
function nodesOf(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  const wrapped = obj(value).nodes
  return Array.isArray(wrapped) ? wrapped : []
}

export function RichTextBody({ value }: { value: RichText | unknown }) {
  if (typeof value === 'string') {
    const paragraphs = value
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
    if (paragraphs.length === 0) return null
    return (
      <>
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </>
    )
  }
  const nodes = nodesOf(value)
  if (nodes.length === 0) return null
  return <>{nodes.map((n, i) => node(n, String(i)))}</>
}

/**
 * True when there is anything at all to render, so a wrapper can be skipped.
 *
 * **This is the function that was false for every real body**, because it
 * asked whether the document *was* an array rather than whether it *held* one.
 * `About` and `RichTextBlock` both return `null` on it, so the failure was a
 * missing section rather than an error — the quietest kind.
 */
export function hasRichText(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0
  return nodesOf(value).length > 0
}
